import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import {
  detectionScopeOf,
  type BreakEvidence,
  type EngineeringBreak,
} from '../types/breaks';
import { ActionPlan } from './ActionPlan';
import { PlatformGlyph } from './PlatformGlyph';
import {
  PLATFORM_CARD_TINT,
  PLATFORM_DIVIDER,
  PLATFORM_LABEL,
  PLATFORM_QUOTE_BORDER,
  PLATFORM_TEXT,
  PLAYBOOK_LABEL,
} from './break-styles';

interface BreakDetailDrawerProps {
  engineeringBreak: EngineeringBreak | null;
  onClose: () => void;
}

/**
 * The "examine" view. Rendered as a full-screen onboarding insight screen -
 * same page background, centered column and staggered reveal as the rest of the
 * onboarding flow - rather than a platform-style side drawer.
 */
export function BreakDetailDrawer({
  engineeringBreak,
  onClose,
}: BreakDetailDrawerProps) {
  const isOpen = engineeringBreak !== null;
  const [evidenceOpen, setEvidenceOpen] = useState(false);

  // Collapse the evidence again whenever a different break is opened.
  useEffect(() => {
    setEvidenceOpen(false);
  }, [engineeringBreak?.id]);

  // Lock body scroll and wire Escape-to-close while the screen is open.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  if (!engineeringBreak) return null;

  const isPlaybook = engineeringBreak.category === 'playbook';
  const isCrossPlatform =
    detectionScopeOf(engineeringBreak) === 'cross-platform';
  const correlatedToolCount = new Set(
    engineeringBreak.evidence.map((evidence) => evidence.platform)
  ).size;

  const eyebrow = isPlaybook
    ? {
        chrome: 'border-violet-300/50 bg-violet-50 text-violet-700',
        Icon: BookOpen,
        text: engineeringBreak.playbookType
          ? `Playbook gap · ${PLAYBOOK_LABEL[engineeringBreak.playbookType]}`
          : 'Playbook gap',
      }
    : {
        chrome: 'border-rose-300/50 bg-rose-50 text-rose-700',
        Icon: AlertTriangle,
        text: 'Commitment at risk',
      };

  // When a break shifted a schedule, the timeline is the main impact - lead with
  // it and demote the playbook to a footnote. Otherwise keep the playbook first.
  const hasTimeline = engineeringBreak.actionPlan.some(
    (action) => action.gantt
  );

  const playbookHero =
    isPlaybook && engineeringBreak.playbook ? (
      <div className="rounded-2xl border border-violet-100 bg-violet-50/40 px-6 py-7 sm:px-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-300/50 bg-white/70 px-3 py-1 text-xs font-medium text-violet-700">
          <BookOpen className="h-3.5 w-3.5" />
          {eyebrow.text}
        </div>
        <h2 className="text-balance text-xl font-medium leading-snug text-violet-950 sm:text-2xl md:text-[1.9rem] md:leading-[1.3]">
          &ldquo;{engineeringBreak.playbook}&rdquo;
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          A rule {engineeringBreak.team} follows - until this ticket broke it.
        </p>
      </div>
    ) : (
      <div className="rounded-2xl border border-rose-100 bg-rose-50/30 px-6 py-7 sm:px-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-rose-300/50 bg-white/70 px-3 py-1 text-xs font-medium text-rose-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          {eyebrow.text}
        </div>
        <h2 className="text-balance text-xl font-semibold leading-snug text-foreground sm:text-2xl md:text-[1.9rem] md:leading-[1.3]">
          {engineeringBreak.title}
        </h2>
      </div>
    );

  const brokeBlock = (
    <>
      <div className="rounded-2xl border border-zinc-200 bg-white/70 px-6 py-6">
        <p className="text-[10px] font-bold uppercase tracking-widest text-rose-500">
          {isPlaybook ? 'What broke it' : 'How it broke'}
        </p>
        {isPlaybook && (
          <h3 className="mt-1.5 text-lg font-semibold leading-snug text-foreground sm:text-xl">
            {engineeringBreak.title}
          </h3>
        )}
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
          {engineeringBreak.detail}
        </p>
      </div>

      {/* The supporting artifacts - collapsed by default. */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setEvidenceOpen((open) => !open)}
          aria-expanded={evidenceOpen}
          className="flex w-full items-center gap-2.5 rounded-xl border border-zinc-200 bg-white/60 px-4 py-3 text-start transition-colors hover:border-zinc-300"
        >
          <span className="text-sm font-semibold tracking-tight text-foreground">
            The evidence
          </span>
          <span className="rounded-full border border-border bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
            {isCrossPlatform
              ? `${correlatedToolCount} tools correlated`
              : 'single source'}
          </span>
          <span className="ms-auto flex items-center gap-1 text-xs font-medium text-muted-foreground">
            {evidenceOpen ? 'Hide' : 'Show'}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${
                evidenceOpen ? 'rotate-180' : ''
              }`}
            />
          </span>
        </button>
        {evidenceOpen && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {engineeringBreak.evidence.map((evidence, index) => (
              <EvidenceArtifact
                key={`${evidence.ref}-${index}`}
                evidence={evidence}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );

  const actionPlanBlock = (
    <ActionPlan
      breakId={engineeringBreak.id}
      actions={engineeringBreak.actionPlan}
    />
  );

  // Slim context header that sits above the timeline in the timeline-led layout.
  const timelineIntro = (
    <div className="rounded-2xl border border-zinc-200 bg-white/70 px-6 py-5">
      <div
        className={cn(
          'mb-2.5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium',
          eyebrow.chrome
        )}
      >
        <eyebrow.Icon className="h-3.5 w-3.5" />
        {eyebrow.text}
      </div>
      <h2 className="text-balance text-xl font-semibold leading-snug text-foreground sm:text-2xl">
        {engineeringBreak.title}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
        {engineeringBreak.summary}
      </p>

      {/* The actual rule this break violated - folded in here so the playbook
          lives in one place (not split between a top tag and a bottom box). */}
      {isPlaybook && engineeringBreak.playbook && (
        <div className="mt-4 flex items-start gap-3 border-t border-zinc-200/70 pt-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
            <BookOpen className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600/80">
              The playbook it broke
            </p>
            <p className="mt-1 text-sm font-medium leading-snug text-foreground">
              &ldquo;{engineeringBreak.playbook}&rdquo;
            </p>
          </div>
        </div>
      )}
    </div>
  );

  // Portal to the body so the examine view escapes any parent stacking context
  // (e.g. the command center's fixed sidebar) and always covers the full screen.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={engineeringBreak.title}
      className="animate-fade-in fixed inset-0 z-50 overflow-y-auto bg-page-bg bg-[radial-gradient(60%_50%_at_50%_0%,rgba(34,209,197,0.12),transparent_70%),radial-gradient(40%_40%_at_85%_85%,rgba(22,120,194,0.10),transparent_75%)]"
    >
      <div className="mx-auto flex min-h-full max-w-[1640px] flex-col px-5 py-8 sm:px-8 sm:py-12">
        {/* Top bar: back to the gallery + provenance */}
        <div className="mb-8 flex items-center justify-between gap-3 sm:mb-10">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            All gaps
          </button>
          <span className="text-sm font-medium text-muted-foreground">
            {engineeringBreak.team} · detected {engineeringBreak.detectedAgo}
          </span>
        </div>

        {hasTimeline ? (
          <div className="stagger-children flex flex-col gap-8 sm:gap-10">
            {/* Slim context, then the timeline as the centerpiece */}
            <section className="mx-auto w-full max-w-[1180px]">
              {timelineIntro}
            </section>

            <section className="w-full">
              <div className="mx-auto mb-5 w-full max-w-[1180px]">
                <PhaseLabel step={1} label="Timeline impact & resolution" />
                <p className="-mt-2 text-sm text-muted-foreground">
                  The slip rippled across the schedule - resolve it here and
                  Intera applies each fix in the right tool.
                </p>
              </div>
              {actionPlanBlock}
            </section>

            <section className="mx-auto w-full max-w-4xl">
              <PhaseLabel step={2} label="Where & how it broke" />
              {brokeBlock}
            </section>
          </div>
        ) : (
          <div className="stagger-children flex flex-col gap-10 sm:gap-12">
            {/* Phase 1 - The playbook (or, for commitments, the promise) */}
            <section className="mx-auto w-full max-w-4xl">
              <PhaseLabel
                step={1}
                label={isPlaybook ? 'The playbook' : 'The commitment'}
              />
              {playbookHero}
            </section>

            {/* Phase 2 - Where & how it broke */}
            <section className="mx-auto w-full max-w-4xl">
              <PhaseLabel step={2} label="Where & how it broke" />
              {brokeBlock}
            </section>

            {/* Phase 3 - Action plan */}
            <section className="w-full">
              <div className="mx-auto mb-5 w-full max-w-4xl">
                <PhaseLabel step={3} label="Intera's action plan" />
                <p className="-mt-2 text-sm text-muted-foreground">
                  Resolve it here - Intera applies the fix in the right tool.
                </p>
              </div>
              {actionPlanBlock}
            </section>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Numbered phase heading that separates the three stages of the examine view. */
function PhaseLabel({ step, label }: { step: number; label: string }) {
  return (
    <div className="mb-4 flex items-center gap-2.5">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
        {step}
      </span>
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

function EvidenceArtifact({ evidence }: { evidence: BreakEvidence }) {
  return (
    <a
      href={evidence.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group flex flex-col rounded-2xl border bg-white/75 p-6 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:shadow-md',
        PLATFORM_CARD_TINT[evidence.platform]
      )}
    >
      <div className="flex-1">
        <div className="mb-1 flex items-center justify-between gap-2">
          <div
            className={cn(
              'inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest',
              PLATFORM_TEXT[evidence.platform]
            )}
          >
            <PlatformGlyph
              platform={evidence.platform}
              className="h-3.5 w-3.5"
            />
            <span>{PLATFORM_LABEL[evidence.platform]}</span>
          </div>
          <span className="text-[11px] font-semibold text-muted-foreground">
            {evidence.ref}
          </span>
        </div>

        <blockquote
          className={cn(
            'mt-4 border-s-[3px] py-1.5 ps-4 text-base font-medium leading-relaxed text-foreground/90',
            PLATFORM_QUOTE_BORDER[evidence.platform]
          )}
        >
          <QuoteWithHighlight
            quote={evidence.quote}
            highlight={evidence.highlight}
          />
        </blockquote>

        {evidence.attribution && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            <AttributionWithHighlight
              attribution={evidence.attribution}
              highlight={evidence.attributionHighlight}
            />
          </p>
        )}
      </div>

      <div
        className={cn(
          'mt-5 flex items-center justify-between border-t pt-4 text-xs font-semibold text-muted-foreground transition-colors group-hover:text-foreground',
          PLATFORM_DIVIDER[evidence.platform]
        )}
      >
        <span>Open in {PLATFORM_LABEL[evidence.platform]}</span>
        <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
      </div>
    </a>
  );
}

function QuoteWithHighlight({
  quote,
  highlight,
}: {
  quote: string;
  highlight?: string;
}) {
  if (!highlight || !quote.includes(highlight)) {
    return <>&ldquo;{quote}&rdquo;</>;
  }
  const parts = quote.split(highlight);
  return (
    <>
      &ldquo;
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < parts.length - 1 && (
            <span className="-my-0.5 mx-0.5 inline-block rounded border border-amber-200 bg-amber-100 px-1.5 py-0.5 align-baseline leading-none text-amber-900 shadow-sm">
              {highlight}
            </span>
          )}
        </Fragment>
      ))}
      &rdquo;
    </>
  );
}

function AttributionWithHighlight({
  attribution,
  highlight,
}: {
  attribution: string;
  highlight?: string;
}) {
  if (!highlight || !attribution.includes(highlight)) {
    return attribution;
  }
  const parts = attribution.split(highlight);
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < parts.length - 1 && (
            <span className="font-bold text-rose-600 dark:text-rose-400">
              {highlight}
            </span>
          )}
        </Fragment>
      ))}
    </>
  );
}
